import React, {useState, useEffect} from 'react';
import { Stack, Typography, Checkbox, FormControlLabel, FormGroup, Button, InputAdornment } from '@mui/material';
import { useForm } from 'react-hook-form';

import { DateField, FormProvider, TextField } from 'components/Form';
import { formatDateTime } from 'features/qna/utils/formatDate';
import { useAuth } from 'lib/auth';
import { AdditionalPeriods } from 'components/AdditionalPeriod/AdditionalPeriod';
import { getJobAdLocations } from 'utils/jobAdLocations';
import { StaticLocationMap } from 'components/JobAdsMap/StaticLocationMap';

const detailsTabName = 'details';

const getDefaultValues = () => ({
  // agreement: null,
  buyer: null,
  // dps: null,
  headline: '',
  role: '',
  experienceLevel: '',
  description: '',
  type: 'On site',
  hoursPerWeek: 40,
  maxRate: '',
  referenceNumber: '',
  startDate: null,
  endDate: null,
  applicationType: 'email',
  applicationEmail: '',
  applicationURL: '',
  lastDayToRespond: null,
  questionsEndDate: '',
  // agreementOrDpsName: '',
  buyerName: '',
  // dpsName: '',
  locations: [],
  additionalPeriods: [],
  competenceAreas: [],
  supplierID: null,
});

export const DetailsTab = ({ currentTab, jobAd }) => {
  const { account } = useAuth(); 
  const [ loading, setLoading ] = useState(true);
  const [ additionalPeriods, setAdditionalPeriods ] = useState(jobAd?.additional_periods);
  const [ initialPeriod, setInitialPeriod ] = useState([
    {
      label: "Initial",
      startDate: jobAd?.start_date,
      endDate: jobAd?.end_date,
      status: "used",
    }
  ]);

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    const {
      headline,
      buyer_id,
      buyer_name,
      // fa_id,
      // fa_name,
      // dps_id,
      // dps_name,
      role,
      experience_level,
      description,
      type,
      hours_per_week,
      max_rate,
      reference_number,
      locations,
      places,
      competence_areas,
      start_date,
      end_date,
      additional_periods,
      application_method,
      application_email,
      application_url,
      last_day_to_respond,
      questions_end_date,
      supplier_id,
    } = jobAd;   
  
    methods.reset({
      // agreement: fa_id || dps_id,
      buyer: buyer_id,
      // dps: dps_id,
      headline: headline,
      role: role,
      experienceLevel: experience_level,
      description: description,
      type: type,
      hoursPerWeek: hours_per_week,
      maxRate: max_rate,
      referenceNumber: reference_number,
      locations: getJobAdLocations({ locations, places }),
      competenceAreas: competence_areas,
      startDate: new Date(start_date),
      endDate: new Date(end_date),
      additionalPeriods: additional_periods,
      applicationType: application_method === 'in_app' ? 'In app' : application_method,
      applicationEmail: application_email,
      applicationURL: application_url,
      lastDayToRespond: new Date(last_day_to_respond),
      questionsEndDate: formatDateTime(questions_end_date),
      supplierID: supplier_id,
      // agreementOrDpsName: fa_name ? `${fa_name} (Agreement)` : dps_name ? `${dps_name} (DPS)` : '',
      buyerName: buyer_name,
      // dpsName: dps_name,
    });
  
    setAdditionalPeriods(jobAd?.additional_periods);
    const tempPeriod = [...initialPeriod];
    tempPeriod[0].startDate = jobAd?.start_date;
    tempPeriod[0].endDate = jobAd?.end_date;
    setInitialPeriod(tempPeriod);
    setLoading(false);
  }, [jobAd]);

  const onSubmit = () => {};

  if (currentTab !== detailsTabName) return null;

  if (loading) {
    return null;
  }

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack direction="row" width="100%" height="100%" bgcolor={'background.paper'} gap={2}>
        <Stack flex={1} direction="column" gap={2}>
          <Stack direction={"row"} gap={2}>
            <TextField
              name="buyerName"
              label="Buyer"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
            {/* <TextField
              name="agreementOrDpsName"
              label="Agreement"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            /> */}
          </Stack>
          {jobAd?.hide_buyer && (
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Buyer name and logo are hidden from other companies"
              />
            </FormGroup>
          )}
          <TextField
            name="headline"
            label="Headline"
            type="text"
            sx={{ mb: 2 }}
            fullWidth
            disabled
          />
          <Stack direction={"row"} gap={2}>
            <TextField
              name="role"
              label="Role"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
            <TextField
              name="experienceLevel"
              label="Experience level"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
          </Stack>
          <TextField
            name="type"
            label="Type"
            type="text"
            sx={{ mb: 2 }}
            fullWidth
            disabled
          />
          <TextField
            name="hoursPerWeek"
            label="Hours per week"
            type="text"
            sx={{ mb: 2 }}
            fullWidth
            disabled
          />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Assignment description
          </Typography>
          <TextField
            name="description"
            disabled
            type="text"
            fullWidth
            sx={{
              '& .MuiInputBase-root, .MuiInputBase-input': {
                p: 0,
              },
            }}
            multiline
            minRows={2}
          />
        </Stack>
        <Stack flex={1} direction="column" gap={2}>
          <Stack direction={"row"} gap={2}>
            <TextField
              name="maxRate"
              label="Max Rate"
              disabled
              type="text"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                  >
                    SEK/h
                  </InputAdornment>
                )
              }}
              wrapperSx={{width: 1}}
            />
            <TextField
              name="referenceNumber"
              label="Reference number"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
          </Stack>
          {methods.getValues("locations") && methods.getValues("locations").length > 0 && (
            <TextField name="locations" label="Locations" disabled type="text" fullWidth isChip />
          )}
          <StaticLocationMap url={jobAd?.static_map_url} />
          {methods.getValues("competenceAreas") && methods.getValues("competenceAreas").length > 0 && (
            <TextField name="competenceAreas" label="Competence Areas" disabled type="text" fullWidth isChip />
          )}
          {/* <Stack direction="row" justifyContent="space-between" gap={2}>
            <DateField name="startDate" label="Start Date" disabled fullWidth />
            <DateField name="endDate" label="End Date" disabled fullWidth />
          </Stack> */}
          <AdditionalPeriods initialPeriod={initialPeriod} additionalPeriods={additionalPeriods} handleAdditionalPeriods={null} readOnly tender />
          <Stack direction={"row"} gap={2} sx={{mt: 2}}>
            <TextField
              name="applicationType"
              label="Application Type"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
            <DateField name="lastDayToRespond" label="Last day to respond" disabled fullWidth />
            <TextField name="questionsEndDate" label="End date for questions" type="text" sx={{ mb: 2 }} fullWidth disabled />
          </Stack>
          {methods.getValues("applicationType") && methods.getValues("applicationType") === 'email' && (
            <TextField
              name="applicationEmail"
              label="Application Email"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
          )}

          {methods.getValues("applicationType") && methods.getValues("applicationType") === 'url' && (
            <TextField
              name="applicationURL"
              label="Application URL"
              type="text"
              sx={{ mb: 2 }}
              fullWidth
              disabled
            />
          )}
        </Stack>
      </Stack>
    </FormProvider>
  );
};
